import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Search, Loader2, ExternalLink, ChevronLeft, ChevronRight,
  FolderKanban, ShieldCheck, Star, Package, Filter, Trash2, Archive, ArchiveRestore,
  Download, Tag, X, Plus, CheckCircle2, RefreshCw, AlertTriangle, ShieldAlert, PlusCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectsApi, type Project } from "@/api";
import { tasksApi } from "@/api/tasks";
import { tagsApi, type Tag } from "@/api/tags";
import {
  alibabaApi,
  type AlibabaSearch,
  type AlibabaProduct,
  type AlibabaFilter,
  type AlibabaRelatedKeyword,
} from "@/api/alibaba";
import PipelineOptions, { getDefaultStages } from "@/components/PipelineOptions";
import { AlibabaCompleteness } from "@/components/AlibabaCompleteness";

// ── Status badge helper ──

const statusVariant: Record<string, "default" | "destructive" | "outline" | "secondary"> = {
  completed: "default",
  failed: "destructive",
  pending: "outline",
  crawling: "secondary",
  parsing: "secondary",
};

function StatusBadge({ status }: { status: string }) {
  return <Badge variant={statusVariant[status] || "outline"}>{status}</Badge>;
}

// ── Health badge helper ──

const HEALTH_ISSUE_LABELS: Record<string, string> = {
  waf_blocked: 'WAF/驗證頁',
  error_page: '錯誤頁面',
  zero_products_small_html: '非產品頁(小HTML)',
  zero_products_large_html: '大HTML無商品',
  structure_changed: '結構變更',
  captcha: '驗證碼',
};

function HealthBadge({ health, issues }: { health?: string; issues?: string[] }) {
  if (!health || health === 'healthy' || health === 'unknown') return null;
  const issueLabels = (issues || []).map(i => HEALTH_ISSUE_LABELS[i] || i).join(', ');
  const isError = health === 'error';
  return (
    <Badge
      variant="outline"
      className={`text-[10px] h-5 gap-1 ${isError ? 'border-red-400 text-red-600' : 'border-yellow-400 text-yellow-600'}`}
      title={issueLabels || health}
    >
      {isError ? <ShieldAlert className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {issueLabels || health}
    </Badge>
  );
}

// ── Filter panel (collapsible) ──

function FilterPanel({ filters }: { filters: AlibabaFilter[] }) {
  if (filters.length === 0) return null;

  return (
    <Card className="w-[280px] shrink-0">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Filters 篩選器 ({filters.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="px-2 py-0 max-h-[600px] overflow-y-auto">
        <Accordion type="multiple" defaultValue={filters.slice(0, 3).map(f => f.group_id)}>
          {filters.map(f => (
            <AccordionItem key={f.group_id} value={f.group_id} className="border-b-0">
              <AccordionTrigger className="px-2 py-2 text-xs hover:no-underline">
                <span className="flex items-center gap-2">
                  {f.group_title}
                  <Badge variant="outline" className="text-[10px] h-4">{f.item_count}</Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-2">
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {(f.items_json || []).slice(0, 20).map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-0.5">
                      <span className="truncate max-w-[180px]" title={item.label}>{item.label}</span>
                      {item.count !== undefined && (
                        <span className="text-muted-foreground ml-2">({item.count})</span>
                      )}
                    </div>
                  ))}
                  {(f.items_json || []).length > 20 && (
                    <div className="text-[11px] text-muted-foreground pt-1">
                      ...還有 {f.items_json.length - 20} 項
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
}

// ── Related Searches ──

function RelatedSearches({
  keywords,
  onSearch,
}: {
  keywords: AlibabaRelatedKeyword[];
  onSearch: (kw: string) => void;
}) {
  if (keywords.length === 0) return null;
  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm">Related Searches 相關搜尋</CardTitle>
      </CardHeader>
      <CardContent className="py-0 pb-3">
        <div className="flex flex-wrap gap-2">
          {keywords.map(kw => (
            <Badge
              key={kw.id}
              variant="outline"
              className="cursor-pointer hover:bg-primary/10"
              onClick={() => onSearch(kw.keyword)}
            >
              {kw.keyword}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Product row ──

function ProductRow({
  product,
  index,
  selected,
  onToggle,
}: {
  product: AlibabaProduct;
  index: number;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <TableRow className="group">
      <TableCell className="w-10">
        <Checkbox checked={selected} onCheckedChange={onToggle} />
      </TableCell>
      <TableCell className="w-12 text-center text-muted-foreground">{index}</TableCell>
      <TableCell className="w-16">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt=""
            className="w-12 h-12 object-cover rounded"
            loading="lazy"
          />
        ) : (
          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
            <Package className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <div className="font-medium text-sm line-clamp-2 max-w-[400px]">
            {product.url ? (
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary hover:underline"
              >
                {product.title}
              </a>
            ) : (
              product.title
            )}
          </div>
          {product.selling_points && product.selling_points.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {product.selling_points.slice(0, 3).map((sp, i) => (
                <Badge key={i} variant="outline" className="text-[10px] h-4">{sp}</Badge>
              ))}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        <div className="font-medium text-primary">{product.price_current || '—'}</div>
        {product.discount && (
          <div className="text-[11px] text-muted-foreground line-through">{product.price_original}</div>
        )}
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        {product.moq_text || '—'}
      </TableCell>
      <TableCell className="text-sm">
        <div className="max-w-[180px]">
          <div className="truncate">
            {product.company_url ? (
              <a href={product.company_url} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline">
                {product.company || '—'}
              </a>
            ) : (
              product.company || '—'
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            {product.years_in_business !== undefined && product.years_in_business > 0 && (
              <span>{product.years_in_business} yrs</span>
            )}
            {product.has_trade_assurance && (
              <span className="flex items-center gap-0.5 text-green-600">
                <ShieldCheck className="h-3 w-3" />TA
              </span>
            )}
            {product.rating !== undefined && product.rating > 0 && (
              <span className="flex items-center gap-0.5">
                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                {product.rating}
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="w-8">
        {product.url && (
          <a href={product.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
          </a>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── Main component ──

export default function AlibabaSearch() {
  // Search form
  const [keyword, setKeyword] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [pages, setPages] = useState("3");
  const [projectId, setProjectId] = useState("__none__");

  // Results view
  const [activeSearchId, setActiveSearchId] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);

  // Pipeline stages
  const [pipelineStages, setPipelineStages] = useState<string[]>(getDefaultStages('alibaba'));

  // Tag input state
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [createTaskProgress, setCreateTaskProgress] = useState<{ done: number; total: number } | null>(null);

  // Supplement search (補充搜尋) dialog state
  const [rescanDialog, setRescanDialog] = useState<{ keyword: string; language: string; category_id?: string } | null>(null);
  const [rescanPageStart, setRescanPageStart] = useState(1);
  const [rescanPageEnd, setRescanPageEnd] = useState(5);
  const [rescanSkipExisting, setRescanSkipExisting] = useState(true);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  // ── Queries ──

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => (await projectsApi.list()).data || [],
  });

  const { data: searches = [], isLoading: searchesLoading } = useQuery<AlibabaSearch[]>({
    queryKey: ["alibaba-searches", projectId, historyPage, showArchived],
    queryFn: async () => {
      const pid = projectId !== "__none__" ? projectId : undefined;
      return (await alibabaApi.searches({
        limit: 50,
        offset: historyPage * 50,
        project_id: pid,
        archived: showArchived,
      })).data || [];
    },
    refetchInterval: 5000,
  });

  const { data: searchDetail, isLoading: detailLoading } = useQuery({
    queryKey: ["alibaba-search-detail", activeSearchId],
    queryFn: async () => {
      if (!activeSearchId) return null;
      return (await alibabaApi.getSearch(activeSearchId)).data || null;
    },
    enabled: !!activeSearchId,
    refetchInterval: activeSearchId ? 5000 : false,
  });

  // Fetch existing tags for autocomplete
  const { data: existingTags = [] } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: async () => (await tagsApi.list()).data || [],
  });

  const { data: filters = [] } = useQuery<AlibabaFilter[]>({
    queryKey: ["alibaba-filters", activeSearchId],
    queryFn: async () => {
      if (!activeSearchId) return [];
      return (await alibabaApi.getFilters(activeSearchId)).data || [];
    },
    enabled: !!activeSearchId,
  });

  // ── Mutations ──

  const searchMutation = useMutation({
    mutationFn: () =>
      alibabaApi.search({
        keyword: keyword.trim(),
        language,
        pages: parseInt(pages),
        project_id: projectId !== "__none__" ? projectId : undefined,
        selected_stages: pipelineStages.length > 0 ? pipelineStages : undefined,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["alibaba-searches"] });
      const pageResults = res.data?.pages || [];
      if (pageResults.length > 0) setActiveSearchId(pageResults[0].search_id);
      toast.success(`已排程 ${pageResults.length} 頁爬取：${keyword}，請稍候...`);
    },
    onError: (err: Error) => toast.error(`搜尋失敗: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (kw: string) => alibabaApi.deleteByKeyword(kw),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["alibaba-searches"] });
      setActiveSearchId(null);
      toast.success(`已刪除 "${res.data?.keyword}" 的 ${res.data?.deleted || 0} 筆搜尋記錄`);
    },
    onError: (err: Error) => toast.error(`刪除失敗: ${err.message}`),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ kw, action }: { kw: string; action: 'archive' | 'unarchive' }) =>
      alibabaApi.archiveByKeyword(kw, action),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["alibaba-searches"] });
      toast.success(variables.action === 'archive' ? '已封存' : '已還原');
    },
    onError: (err: Error) => toast.error(`操作失敗: ${err.message}`),
  });

  const retrySearchMutation = useMutation({
    mutationFn: (searchId: string) => alibabaApi.retrySearch(searchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alibaba-searches"] });
      toast.success("重試已排程");
    },
    onError: (err: Error) => toast.error(`重試失敗: ${err.message}`),
  });

  const retryUnhealthyMutation = useMutation({
    mutationFn: (kw: string) => alibabaApi.retryUnhealthyByKeyword(kw),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["alibaba-searches"] });
      toast.success(res.data?.message || "異常頁面重試已排程");
    },
    onError: (err: Error) => toast.error(`重試失敗: ${err.message}`),
  });

  const retryFailedMutation = useMutation({
    mutationFn: (kw: string) => alibabaApi.retryFailedByKeyword(kw),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["alibaba-searches"] });
      toast.success(res.data?.message || "重試已排程");
    },
    onError: (err: Error) => toast.error(`批次重試失敗: ${err.message}`),
  });

  const rescanMutation = useMutation({
    mutationFn: (data: { keyword: string; language: string; category_id?: string; pageStart: number; pageEnd: number; skipExisting: boolean }) => {
      const specificPages: number[] = [];
      for (let p = data.pageStart; p <= data.pageEnd; p++) specificPages.push(p);
      return alibabaApi.search({
        keyword: data.keyword,
        language: data.language,
        category_id: data.category_id,
        force_rescan: true,
        specific_pages: specificPages,
        skip_existing_urls: data.skipExisting,
      });
    },
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["alibaba-searches"] });
      toast.success(`已排程補充搜尋: "${vars.keyword}" P${vars.pageStart}~P${vars.pageEnd}${vars.skipExisting ? "（跳過已存在產品）" : ""}`);
      setRescanDialog(null);
    },
    onError: (err: Error) => toast.error(`補充搜尋失敗: ${err.message}`),
  });

  const analyzeMutation = useMutation({
    mutationFn: () =>
      alibabaApi.analyzeProducts({
        product_ids: Array.from(selectedProducts),
        project_id: projectId !== "__none__" ? projectId : undefined,
      }),
    onSuccess: (res) => {
      toast.success(`已建立 ${res.data?.created || 0} 個 SEO 分析任務`);
      setSelectedProducts(new Set());
    },
    onError: (err: Error) => toast.error(`批次分析失敗: ${err.message}`),
  });

  // ── Handlers ──

  const handleSearch = () => {
    if (!keyword.trim()) {
      toast.error("請輸入關鍵字 Keyword");
      return;
    }
    searchMutation.mutate();
  };

  const handleRelatedSearch = (kw: string) => {
    setKeyword(kw);
    // Only fill in the keyword, do NOT auto-trigger search
    toast.info(`已填入關鍵字: ${kw}，按搜尋按鈕開始`);
  };

  const toggleProduct = (id: number) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllProducts = () => {
    const products = searchDetail?.products || [];
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setShowTagDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCreateTasksWithTags = async () => {
    const selectedProductList = (searchDetail?.products || []).filter(p => selectedProducts.has(p.id));
    const urlProducts = selectedProductList.filter(p => p.url);

    if (urlProducts.length === 0) {
      toast.error("所選商品沒有可用的 URL");
      return;
    }

    const total = urlProducts.length;
    setCreateTaskProgress({ done: 0, total });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < urlProducts.length; i++) {
      const product = urlProducts[i];
      try {
        // Create task
        const taskRes = await tasksApi.create({
          url: product.url!,
          keyword: searchDetail?.keyword,
          project_id: projectId !== "__none__" ? projectId : undefined,
          tag: 'alibaba',
          selected_stages: pipelineStages.length > 0 ? pipelineStages : undefined,
        });
        const taskId = taskRes.data?.task_id;

        // Link tags if any
        if (taskId && selectedTags.length > 0) {
          for (const tagName of selectedTags) {
            try {
              await tagsApi.link(taskId, tagName);
            } catch {
              // Tag link failure is non-critical
              console.warn(`Failed to link tag "${tagName}" to task ${taskId}`);
            }
          }
        }

        successCount++;
      } catch {
        failCount++;
      }

      setCreateTaskProgress({ done: i + 1, total });
    }

    setCreateTaskProgress(null);

    if (failCount === 0) {
      toast.success(`已建立 ${successCount} 個任務${selectedTags.length > 0 ? `，標記 ${selectedTags.length} 個 Tags` : ""}`);
    } else {
      toast.warning(`建立 ${successCount} 個成功，${failCount} 個失敗`);
    }

    // Clear selection
    setSelectedProducts(new Set());
    setSelectedTags([]);
    setTagInput("");

    // Refresh tags query (usage count may have changed)
    queryClient.invalidateQueries({ queryKey: ["tags"] });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };

  const products = searchDetail?.products || [];
  const relatedKeywords = searchDetail?.related_keywords || [];

  const handleExportCsv = () => {
    if (products.length === 0) return;
    const headers = ['position', 'title', 'price_current', 'moq_text', 'company', 'company_url', 'url'];
    const escCsv = (v: string) => `"${(v || '').replace(/"/g, '""')}"`;
    const rows = products.map((p, i) => [
      String(i + 1),
      escCsv(p.title),
      escCsv(p.price_current || ''),
      escCsv(p.moq_text || ''),
      escCsv(p.company || ''),
      escCsv(p.company_url || ''),
      escCsv(p.url || ''),
    ].join(','));
    const csv = '\uFEFF' + headers.join(',') + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alibaba-${searchDetail?.keyword || 'products'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Alibaba Search 阿里巴巴搜尋</h1>
        <p className="text-muted-foreground">
          透過 BrowserAct 搜尋阿里巴巴商品，批次 SEO 分析
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search 搜尋
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Row 1: Keyword + Search button */}
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-2">
              <Label>Keyword 關鍵字 *</Label>
              <Input
                placeholder="e.g. isolation gown, terminal block, t-shirt"
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={searchMutation.isPending}
            >
              {searchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              搜尋
            </Button>
            <span className="text-xs text-muted-foreground self-end pb-2">BrowserAct 爬取約 90 秒/頁</span>
          </div>

          {/* Row 2: Options */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Language 語言</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_US">English (US)</SelectItem>
                  <SelectItem value="zh_TW">繁體中文 (TW)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pages 頁數</Label>
              <Select value={pages} onValueChange={setPages}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                    <SelectItem key={n} value={String(n)}>
                      {n} 頁 ({n * 48} 商品)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <FolderKanban className="h-3 w-3" />
                Project 專案
              </Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger><SelectValue placeholder="不指定" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">不指定</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Tags (auto-populated with keyword) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1"><Tag className="h-3 w-3" />Tags 標籤</Label>
            <div className="flex flex-wrap items-center gap-2 p-2 rounded-md border bg-muted/30 min-h-[36px]">
              {/* Auto tag from keyword */}
              {keyword.trim() && (
                <Badge variant="default" className="text-xs gap-1">
                  {keyword.trim()}
                  <span className="text-[10px] text-muted-foreground/70 ml-0.5">自動</span>
                </Badge>
              )}
              {/* Manual tags */}
              {selectedTags.filter(t => t !== keyword.trim()).map(tag => (
                <Badge key={tag} variant="outline" className="text-xs gap-1">
                  {tag}
                  <button onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="ml-0.5 hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {/* Add tag input */}
              <Input
                className="h-7 w-[140px] border-none bg-transparent text-xs p-0 px-1 focus-visible:ring-0"
                placeholder="+ 新增 Tag..."
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && tagInput.trim()) {
                    e.preventDefault();
                    if (!selectedTags.includes(tagInput.trim())) {
                      setSelectedTags(prev => [...prev, tagInput.trim()]);
                    }
                    setTagInput("");
                  }
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">搜尋關鍵字會自動成為 Tag，搜尋完成後所有產品任務自動掛上這些 Tags</p>
          </div>

          {/* Pipeline Stages */}
          <PipelineOptions page="alibaba" selectedStages={pipelineStages} onChange={setPipelineStages} />
        </CardContent>
      </Card>

      {/* Product Insights Overview */}
      <AlibabaCompleteness />

      {/* Search History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Search History 搜尋歷史</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{searches.length} 筆</span>
              <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived(!showArchived)}>
                {showArchived ? "顯示活動" : "顯示封存"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {searchesLoading ? (
            <div className="text-center py-6">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : searches.length === 0 ? (
            <div className="text-center text-muted-foreground py-6">尚無搜尋紀錄</div>
          ) : (
            <>
              <div className="space-y-1">
                {/* Group searches by keyword — show one row per keyword with totals */}
                {(() => {
                  const grouped = new Map<string, { keyword: string; language: string; category_id?: string; pages: typeof searches; totalProducts: number; latestSearchId: string; latestDate: string; statuses: string[]; healthStatuses: string[] }>();
                  for (const s of searches) {
                    const key = `${s.keyword}_${s.language}_${s.category_id || ''}`;
                    const existing = grouped.get(key);
                    if (existing) {
                      existing.pages.push(s);
                      existing.totalProducts += s.product_count;
                      existing.statuses.push(s.status);
                      existing.healthStatuses.push(s.health_status || 'unknown');
                      if (s.created_at > existing.latestDate) {
                        existing.latestDate = s.created_at;
                        existing.latestSearchId = s.search_id;
                      }
                    } else {
                      grouped.set(key, {
                        keyword: s.keyword,
                        language: s.language,
                        category_id: s.category_id,
                        pages: [s],
                        totalProducts: s.product_count,
                        latestSearchId: s.search_id,
                        latestDate: s.created_at,
                        statuses: [s.status],
                        healthStatuses: [s.health_status || 'unknown'],
                      });
                    }
                  }
                  return Array.from(grouped.values()).map(g => {
                    const allCompleted = g.statuses.every(s => s === 'completed');
                    const anyFailed = g.statuses.some(s => s === 'failed');
                    const anyCrawling = g.statuses.some(s => s === 'crawling' || s === 'pending' || s === 'parsing');
                    const overallStatus = allCompleted ? 'completed' : anyFailed ? 'failed' : anyCrawling ? 'crawling' : g.statuses[0];
                    const completedPages = g.statuses.filter(s => s === 'completed').length;
                    const unhealthyCount = g.healthStatuses.filter(h => h === 'warning' || h === 'error').length;
                    const hasHealthIssues = unhealthyCount > 0;
                    return (
                      <div key={g.latestSearchId}>
                      <div
                        className={`flex items-center justify-between p-3 rounded-md cursor-pointer transition-colors ${
                          activeSearchId === g.latestSearchId
                            ? "bg-primary/10 border border-primary/30"
                            : "hover:bg-muted/50"
                        }`}
                        onClick={() => setActiveSearchId(g.pages.find(p => p.status === 'completed')?.search_id || g.latestSearchId)}
                      >
                        <div className="flex items-center gap-3">
                          <StatusBadge status={overallStatus} />
                          <span className="font-medium">{g.keyword}</span>
                          {g.category_id && (
                            <span className="text-xs text-muted-foreground">cat: {g.category_id}</span>
                          )}
                          <Badge variant="outline" className="text-xs">{g.language}</Badge>
                          <Badge variant="outline" className="text-xs">{completedPages}/{g.pages.length} pages</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">{g.totalProducts} products</span>
                          {hasHealthIssues && (
                            <Badge
                              variant="outline"
                              className="text-[10px] h-5 gap-1 border-yellow-400 text-yellow-600"
                              title={`${unhealthyCount} 頁異常`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {unhealthyCount} 頁異常
                            </Badge>
                          )}
                          <span>{new Date(g.latestDate).toLocaleString("zh-TW")}</span>
                          {hasHealthIssues && !anyFailed && (
                            <Button
                              variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-yellow-600"
                              title={`重試 ${unhealthyCount} 個異常頁面`}
                              disabled={retryUnhealthyMutation.isPending}
                              onClick={(e) => { e.stopPropagation(); retryUnhealthyMutation.mutate(g.keyword); }}
                            >
                              {retryUnhealthyMutation.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3.5 w-3.5" />
                              )}
                              <span className="text-xs ml-1">重試異常({unhealthyCount})</span>
                            </Button>
                          )}
                          {anyFailed && (
                            <Button
                              variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-orange-600"
                              title={`重試 ${g.statuses.filter(s => s === 'failed').length} 個失敗頁面`}
                              disabled={retryFailedMutation.isPending}
                              onClick={(e) => { e.stopPropagation(); retryFailedMutation.mutate(g.keyword); }}
                            >
                              {retryFailedMutation.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3.5 w-3.5" />
                              )}
                              <span className="text-xs ml-1">重試({g.statuses.filter(s => s === 'failed').length})</span>
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-blue-600"
                            title="補充搜尋 — 追加新頁面或新產品"
                            onClick={(e) => { e.stopPropagation(); setRescanPageStart(1); setRescanPageEnd(5); setRescanSkipExisting(true); setRescanDialog({ keyword: g.keyword, language: g.language, category_id: g.category_id }); }}
                          >
                            <PlusCircle className="h-3.5 w-3.5" />
                            <span className="text-xs ml-1">補充</span>
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                            title={showArchived ? "還原" : "封存"}
                            onClick={(e) => { e.stopPropagation(); archiveMutation.mutate({ kw: g.keyword, action: showArchived ? 'unarchive' : 'archive' }); }}
                          >
                            {showArchived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={(e) => e.stopPropagation()}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>確認刪除</AlertDialogTitle>
                                <AlertDialogDescription>
                                  確定要刪除 &quot;{g.keyword}&quot; 的所有搜尋記錄嗎？
                                  <br />
                                  將刪除 {g.pages.length} 頁、{g.totalProducts} 個商品資料。此操作無法復原。
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>取消</AlertDialogCancel>
                                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => deleteMutation.mutate(g.keyword)}>
                                  確認刪除
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                      {/* Per-page status list (shown when any page failed or has health issues) */}
                      {(anyFailed || hasHealthIssues) && (
                        <div className="ml-8 mt-1 mb-2 space-y-0.5">
                          {g.pages
                            .sort((a, b) => a.page - b.page)
                            .map(pg => (
                            <div key={pg.search_id} className="flex items-center gap-2 text-xs py-0.5">
                              <StatusBadge status={pg.status} />
                              <span className="text-muted-foreground">P{pg.page}</span>
                              <span className="text-muted-foreground">{pg.product_count} products</span>
                              {pg.html_size && (
                                <span className="text-muted-foreground">{Math.round((pg.html_size || 0) / 1024)}KB</span>
                              )}
                              <HealthBadge health={pg.health_status} issues={pg.health_issues} />
                              {pg.status === 'failed' && (
                                <>
                                  {pg.error_message && (
                                    <span className="text-destructive truncate max-w-[200px]" title={pg.error_message}>
                                      {pg.error_message}
                                    </span>
                                  )}
                                  <Button
                                    variant="ghost" size="sm" className="h-5 px-1.5 text-xs text-muted-foreground hover:text-orange-600"
                                    disabled={retrySearchMutation.isPending}
                                    onClick={(e) => { e.stopPropagation(); retrySearchMutation.mutate(pg.search_id); }}
                                  >
                                    <RefreshCw className="h-3 w-3 mr-0.5" />
                                    重試
                                  </Button>
                                </>
                              )}
                              {pg.status === 'completed' && (
                                <>
                                  <Button
                                    variant="ghost" size="sm" className="h-5 px-1.5 text-xs text-muted-foreground hover:text-primary"
                                    onClick={(e) => { e.stopPropagation(); setActiveSearchId(pg.search_id); }}
                                  >
                                    查看
                                  </Button>
                                  {(pg.health_status === 'warning' || pg.health_status === 'error') && (
                                    <Button
                                      variant="ghost" size="sm" className="h-5 px-1.5 text-xs text-muted-foreground hover:text-yellow-600"
                                      disabled={retrySearchMutation.isPending}
                                      onClick={(e) => { e.stopPropagation(); retrySearchMutation.mutate(pg.search_id); }}
                                    >
                                      <RefreshCw className="h-3 w-3 mr-0.5" />
                                      重試
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      </div>
                    );
                  });
                })()}
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={historyPage === 0}
                  onClick={() => setHistoryPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">第 {historyPage + 1} 頁</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={searches.length < 20}
                  onClick={() => setHistoryPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Results Area */}
      {activeSearchId && (
        <>
          {/* Related Searches */}
          <RelatedSearches keywords={relatedKeywords} onSearch={handleRelatedSearch} />

          {/* Products + Filters side-by-side */}
          <div className="flex gap-4">
            {/* Left: Filters */}
            <FilterPanel filters={filters} />

            {/* Right: Products */}
            <div className="flex-1 space-y-4">
              {/* Batch action bar */}
              {selectedProducts.size > 0 && (
                <div className="space-y-3 p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">
                      已選取 {selectedProducts.size} 個商品
                    </span>
                    <Button
                      size="sm"
                      onClick={() => analyzeMutation.mutate()}
                      disabled={analyzeMutation.isPending}
                    >
                      {analyzeMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <Search className="h-3 w-3 mr-1" />
                      )}
                      Batch SEO Analysis 批次 SEO 分析
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedProducts(new Set())}
                    >
                      取消選取
                    </Button>
                  </div>

                  {/* Tag input + Create Tasks */}
                  <div className="flex items-center gap-3">
                    <Tag className="h-4 w-4 text-muted-foreground shrink-0" />

                    {/* Selected tag badges */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {selectedTags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs gap-1 pr-1">
                          {tag}
                          <button
                            className="ml-0.5 hover:text-destructive"
                            onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>

                    {/* Tag input with dropdown */}
                    <div className="relative" ref={tagDropdownRef}>
                      <Input
                        ref={tagInputRef}
                        placeholder="輸入 Tag 按 Enter 新增..."
                        className="w-52 h-8 text-sm"
                        value={tagInput}
                        onChange={e => {
                          setTagInput(e.target.value);
                          setShowTagDropdown(true);
                        }}
                        onFocus={() => setShowTagDropdown(true)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && tagInput.trim()) {
                            e.preventDefault();
                            const trimmed = tagInput.trim();
                            if (!selectedTags.includes(trimmed)) {
                              setSelectedTags(prev => [...prev, trimmed]);
                            }
                            setTagInput("");
                            setShowTagDropdown(false);
                          }
                        }}
                      />
                      {showTagDropdown && (() => {
                        const filtered = existingTags.filter(
                          t => t.tag_name.toLowerCase().includes(tagInput.toLowerCase()) && !selectedTags.includes(t.tag_name)
                        );
                        if (filtered.length === 0) return null;
                        return (
                          <div className="absolute z-50 mt-1 w-52 max-h-40 overflow-y-auto bg-popover border rounded-md shadow-md">
                            {filtered.map(t => (
                              <button
                                key={t.id}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center justify-between"
                                onMouseDown={e => {
                                  e.preventDefault(); // prevent blur
                                  setSelectedTags(prev => [...prev, t.tag_name]);
                                  setTagInput("");
                                  setShowTagDropdown(false);
                                }}
                              >
                                <span>{t.tag_name}</span>
                                {t.usage_count !== undefined && (
                                  <span className="text-xs text-muted-foreground">({t.usage_count})</span>
                                )}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Create Tasks button */}
                    <Button
                      size="sm"
                      variant="default"
                      disabled={createTaskProgress !== null}
                      onClick={handleCreateTasksWithTags}
                    >
                      {createTaskProgress ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          {createTaskProgress.done}/{createTaskProgress.total}
                        </>
                      ) : (
                        <>
                          <Plus className="h-3 w-3 mr-1" />
                          建立任務{selectedTags.length > 0 ? ` + ${selectedTags.length} Tags` : ""}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Product table */}
              <Card>
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Products 商品列表
                      {searchDetail && (
                        <span className="text-sm font-normal text-muted-foreground ml-2">
                          ({products.length} 筆)
                        </span>
                      )}
                    </CardTitle>
                    {products.length > 0 && (
                      <Button variant="outline" size="sm" onClick={handleExportCsv}>
                        <Download className="h-3.5 w-3.5 mr-1" />
                        Export CSV 匯出
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {detailLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                    </div>
                  ) : products.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      {searchDetail?.status === "completed"
                        ? "無商品結果"
                        : "搜尋進行中..."}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox
                              checked={products.length > 0 && selectedProducts.size === products.length}
                              onCheckedChange={toggleAllProducts}
                            />
                          </TableHead>
                          <TableHead className="w-12 text-center">#</TableHead>
                          <TableHead className="w-16">Image 圖片</TableHead>
                          <TableHead>Title 標題</TableHead>
                          <TableHead>Price 價格</TableHead>
                          <TableHead>MOQ</TableHead>
                          <TableHead>Supplier 供應商</TableHead>
                          <TableHead className="w-8"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map((p, i) => (
                          <ProductRow
                            key={p.id}
                            product={p}
                            index={i + 1}
                            selected={selectedProducts.has(p.id)}
                            onToggle={() => toggleProduct(p.id)}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* 補充搜尋 Dialog */}
      <Dialog open={!!rescanDialog} onOpenChange={(open) => { if (!open) setRescanDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>補充搜尋 — {rescanDialog?.keyword}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-muted-foreground">
              追加爬取指定頁面，並可選擇是否跳過已存在的產品 URL（避免重複）。
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">開始頁</Label>
                <Input
                  type="number" min={1} max={10} value={rescanPageStart}
                  onChange={(e) => setRescanPageStart(Math.max(1, Math.min(10, Number(e.target.value))))}
                  className="mt-1"
                />
              </div>
              <div className="pt-5 text-muted-foreground">—</div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">結束頁</Label>
                <Input
                  type="number" min={1} max={10} value={rescanPageEnd}
                  onChange={(e) => setRescanPageEnd(Math.max(1, Math.min(10, Number(e.target.value))))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              將爬取第 {rescanPageStart}～{rescanPageEnd} 頁，共 {Math.max(0, rescanPageEnd - rescanPageStart + 1)} 頁
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="skip-existing"
                checked={rescanSkipExisting}
                onCheckedChange={(v) => setRescanSkipExisting(!!v)}
              />
              <Label htmlFor="skip-existing" className="text-sm cursor-pointer">
                跳過已存在的產品 URL（僅新增未曾爬取的產品）
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescanDialog(null)}>取消</Button>
            <Button
              disabled={rescanMutation.isPending || rescanPageEnd < rescanPageStart}
              onClick={() => {
                if (!rescanDialog) return;
                rescanMutation.mutate({
                  keyword: rescanDialog.keyword,
                  language: rescanDialog.language,
                  category_id: rescanDialog.category_id,
                  pageStart: rescanPageStart,
                  pageEnd: rescanPageEnd,
                  skipExisting: rescanSkipExisting,
                });
              }}
            >
              {rescanMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PlusCircle className="h-4 w-4 mr-2" />}
              開始補充搜尋
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
